import { useEffect, useLayoutEffect, useRef, useState } from "react";
import Breadcrumbs from "../../components/breadcrumbs/Breadcrumbs";
import Button from "../../components/buttons/Button";
import Image from "../../components/images/Image";
import Shimmer from "../AdminManagement/components/ViewAdminDetailsShimmer";
import useApi from "../../services/useApi";
import useTranslation from "../../components/CustomHooks/useTranslation";
import { adminContents } from "../AdminManagement/components/AdminManagementContent";
import { useNavigate, useParams } from "react-router";
import { MNOs } from "../UserManagement/MasterDistributorManagement/Components/types";
import { formatDate, formatDateToDDMMYYYY } from "../../components/commonfunctions/convertDate";
import { RootState } from "../../store";
import { useSelector } from "react-redux";
import RoundedLoader from "../../components/loaders/RoundedLoader";
import ButtonWithImg from "../../components/buttons/ButtonWithImg";
import CustomModal from "../../components/CustomModal";
import { useDispatch } from "react-redux";
import { showToast } from "../../components/toastMessage/toastSlice";
import ToolTip from "../../components/ToolTip/ToolTip";


export default function ViewMnoDetails() {
    const { getAPI, PostAPI } = useApi();
    const { translate, notEnglish, replace } = useTranslation(adminContents);
    const [loading, setLoading] = useState(true);
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const detailsRef = useRef<HTMLDetailsElement>(null);
    const divRef = useRef<HTMLDivElement | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const pathname = useSelector((state: RootState) => state.geolocation.previousUrl);
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                detailsRef.current &&
                !detailsRef.current.contains(event.target as Node)
            ) {
                detailsRef.current.open = false;
            }
        };

        document.addEventListener("click", handleClickOutside);

        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, []);

    const [mnoDetails, setMnoDetails] = useState<MNOs>({
        id: '',
        name: '',
        subnet: '',
        contract_start_date: '',
        contract_end_date: '',
        min_recharge_amount: '',
        max_recharge_amount: '',
        office_no: '',
        market_block_name: '',
        floor_no: '',
        street_name: '',
        city_town: '',
        country: '',
        province: '',
        region: '',
        district: '',
        postal_code: '',
        etopup_upfront_fee_percent: '',
        etopup_target_fee_percent: '',
        etopup_principal_fee_percent: '',
        etopup_dispersive_fee_percent: '',
        bundle_upfront_fee_percent: '',
        bundle_target_fee_percent: '',
        bundle_principal_fee_percent: '',
        bundle_dispersive_fee_percent: '',
        etopup_from_date: '',
        etopup_to_date: '',
        etopup_target_value: '',
        bundle_from_date: '',
        bundle_to_date: '',
        bundle_target_value: '',
        created_at: '',
        updated_at: '',
        deployment_status: '',
        etopup_balance: '',
        bundle_balance: ''
    });
    /**
     * This function fetches details of a master distributor from an API and updates the state accordingly,
     * handling errors and navigation based on the response.
     */
    const fetchmasterDistributorDetails = async () => {
        setLoading(true);
        try {
            const response = await getAPI(`mnos/${id}`);
            if (!response?.error) {
                const temp = response?.data?.data;
                delete temp["created_at"];
                delete temp["updated_at"];
                setMnoDetails(temp);
            } else {
                navigate("/not-found", {
                    state: { route: "/admin-management/list-of-admins" },
                });
            }
        } catch (error) {
            console.error("Error fetching admin details:", error);
        }
        setLoading(false);
    };
    const addressParts = [
        'Office Number ' + mnoDetails?.office_no,
        mnoDetails?.floor_no,
        mnoDetails?.market_block_name,
        mnoDetails?.street_name,
        mnoDetails?.district,
        mnoDetails?.city_town,
        mnoDetails?.region,
        mnoDetails?.country,
        mnoDetails?.postal_code
    ];

    const addressString = addressParts
        .filter(part => part) // Remove null, undefined, and empty values
        .join(", "); // Join with comma only for valid values

    useLayoutEffect(() => {
        fetchmasterDistributorDetails();
    }, []);

    const handleSubnetConnection = async () => {
        let res = await PostAPI(`mnos/deploy/${id}`, '')
        if (!res?.error) {
            setIsModalOpen(false);
            dispatch(showToast({ message: res?.data?.message, type: "success" }));
        } else {
            setIsModalOpen(false);
            dispatch(showToast({ message: res?.data?.response?.data?.message, type: "error" }));
        }
        setIsModalOpen(false);

    }

    const resetOptions = ["Manage Bundle Plans", "On Target Achievement", "Service Fee Transaction", "Purchased Amount Transaction"];
    const handelActions = (option: string) => {
        switch (option) {
            case 'Manage Bundle Plans':
                navigate(`/mno-management/list-of-mnos/view-mno/${id}/bundle-plans`);
                break;
            case 'On Target Achievement':
                navigate(`/mno-management/list-of-mnos/view-mno/${id}/on-target-achievement`);
                break;
            case 'Service Fee Transaction':
                navigate(`/mno-management/list-of-mnos/view-mno/${id}/service-fee-transaction`);
                break;
            case 'Purchased Amount Transaction':
                navigate(`/mno-management/list-of-mnos/view-mno/${id}/purchased-amount-transaction`)
                break;
        }
    };
    return (
      <div dir={notEnglish ? "rtl" : "ltr"}>
        <Breadcrumbs
          navPath={[`MNO Management`, `List of MNO's`, `View Details`]}
          links={
            pathname
              ? [pathname, pathname]
              : [`/mno-management/list-of-mnos`, `/mno-management/list-of-mnos`]
          }
          heading={`View MNO`}
        />

        {!loading ? (
          <div
            className=" mt-1.5 bg-white h-maintableheight2 rounded-lg flex flex-col"
            ref={divRef}
          >
            <div className="px-6 py-5 border-b border-[#E5E9EB]  flex justify-between items-center">
              <div>
                <div
                  title={"name"}
                  data-testid="name"
                  className="flex flex-wrap items-start gap-4 text-heading2"
                >
                  <div
                    data-testid="user-name"
                    className="break-all max-w-[500px]"
                  >
                    {mnoDetails.name || "-"}
                  </div>
                </div>
              </div>
              <div className="flex gap-[24px]  print:hidden">
                <div className="group relative">
                  <ButtonWithImg
                    buttonName={"Connect"}
                    className={
                      "border-[1px] text-white bg-blue-800 h-fit px-4 py-2 flex-row-reverse"
                    }
                    buttonOnclick={() => {
                      mnoDetails.subnet &&
                      mnoDetails?.deployment_status !== "DEPLOYED"
                        ? setIsModalOpen(true)
                        : mnoDetails.deployment_status === "DEPLOYED"
                        ? dispatch(
                            showToast({
                              type: "error",
                              message:
                                "The connection has already been established for this MNO. You cannot connect again.",
                            })
                          )
                        : dispatch(
                            showToast({
                              type: "error",
                              message:
                                "This MNO is not associated with any subnet. Please update the MNO and select a subnet to proceed.",
                            })
                          );
                    }}
                    ImgSrc={`connect`}
                    testId="connect-btn"
                  />
                  <ToolTip
                    ToolTipItem="Clicking Connect will link this MNO to the selected subnet. This will establish a connection between the MNO and its necessary resources. 
                    Once connected, the MNO will be available in the recharge system and will appear as an option while performing recharges."
                    width="w-[364px]"
                    top="top-[50px]"
                    left="left-[-240px]"
                    bgColor="bg-[#E5F0FC]"
                    textColor="text-black"
                  />
                </div>
                <div className="flex gap-2">
                  <Image
                    testId={`edit-btn`}
                    src={`editImg`}
                    key={"edit"}
                    className="z-50 w-[24px] mt-[6px] h-[24px] cursor-pointer"
                    onClick={() => {
                      navigate(`/mno-management/list-of-mnos/edit-mno/${id}`);
                    }}
                  />
                  <details ref={detailsRef} className="relative">
                    <summary className="px-2 py-2 bg-primary-500 text-white rounded-full font-semibold cursor-pointer w-[40px] h-[40px] flex items-center justify-center">
                      <Image
                        src="more-vertical"
                        objectFit={"object-contain"}
                        testId="action-btn"
                      />
                    </summary>
                    <div className="absolute z-10 bg-white shadow rounded-md w-[300px] mt-1 right-0">
                      <ul className="p-2 border border-s_dark_hover rounded-lg">
                        {resetOptions.map((item) =>
                          loading ? (
                            <RoundedLoader />
                          ) : (
                            <li
                              onClick={() => handelActions(item)}
                              data-testid={item}
                              key={item}
                              className="text-primary_text hover:text-p_primary cursor-pointer p-2.5 hover:rounded-lg hover:bg-neutral-100 rounded-xs capitalize"
                            >
                              {translate(`${item}`)}
                            </li>
                          )
                        )}
                      </ul>
                    </div>
                  </details>
                </div>
              </div>
            </div>
            <div className=" w-full mb-4 mt-4 overflow-auto scrollBar">
              <div className="px-6 pt-2 flex flex-wrap h- w-full">
                <div className="flex flex-col mb-4 w-1/3">
                  <p className=" text-primary_text font-normal mb-1 text-sm">
                    eTop Up Balance
                  </p>
                  <p className=" text-primary_text font-medium text-sm">
                    {mnoDetails.etopup_balance || "-"}
                  </p>
                </div>
                <div className="flex flex-col w-1/3 mb-4">
                  <p className=" text-primary_text font-normal mb-1 text-sm">
                    Bundle Balance
                  </p>
                  <p className=" text-primary_text font-medium text-sm">
                    {mnoDetails.bundle_balance || "-"}
                  </p>
                </div>
                <div className="flex flex-col w-1/3 mb-4">
                  <p className=" text-primary_text font-normal mb-1 text-sm">
                    Amount Payable to MNO
                  </p>
                  <p className=" text-primary_text font-medium text-sm">-</p>
                </div>
                <div className="flex flex-col w-1/3 mb-4">
                  <p className=" text-primary_text font-normal mb-1 text-sm">
                    Service Fee Received{" "}
                  </p>
                  <p className=" text-primary_text font-medium text-sm">-</p>
                </div>
                <div className="flex flex-col w-1/3 mb-4">
                  <p className=" text-primary_text font-normal mb-1 text-sm">
                    Service Fee Paid
                  </p>
                  <p className=" text-primary_text font-medium text-sm">-</p>
                </div>
                <div className="flex flex-col mb-4 w-1/3">
                  <p className=" text-primary_text font-normal mb-1 text-sm">
                    Reversal Amount Received
                  </p>
                  <p className=" text-primary_text font-medium text-sm">-</p>
                </div>
                <div className="flex flex-col mb-4 w-1/3">
                  <p className=" text-primary_text font-normal mb-1 text-sm">
                    Reversal Amount Paid
                  </p>
                  <p className=" text-primary_text font-medium text-sm">-</p>
                </div>
                <div className="flex flex-col mb-4 w-1/3">
                  <p className=" text-primary_text font-normal mb-1 text-sm">
                    Net Service Fee Earned
                  </p>
                  <p className=" text-primary_text font-medium text-sm">-</p>
                </div>
                <hr className="border-t-1 border-gray-300 w-full" />
              </div>
              <div className="px-6 pt-6 flex flex-wrap w-full">
                <div className="flex flex-col mb-4 w-1/3">
                  <p className=" text-primary_text font-normal mb-1 text-sm">
                    Contract Start Date
                  </p>
                  <p className=" text-primary_text font-medium text-sm">
                    {mnoDetails.contract_start_date
                      ? formatDateToDDMMYYYY(mnoDetails.contract_start_date)
                      : "-"}
                  </p>
                </div>
                <div className="flex flex-col w-1/3 mb-4">
                  <p className=" text-primary_text font-normal mb-1 text-sm">
                    Contract End Date
                  </p>
                  <p className=" text-primary_text font-medium text-sm">
                    {mnoDetails.contract_end_date
                      ? formatDateToDDMMYYYY(mnoDetails.contract_end_date)
                      : "-"}
                  </p>
                </div>
                <div className="flex flex-col w-1/3 mb-4">
                  <p className=" text-primary_text font-normal mb-1 text-sm">
                    Minimum Recharge Amount
                  </p>
                  <p className=" text-primary_text font-medium text-sm">
                    {mnoDetails.min_recharge_amount
                      ? "AFA " + mnoDetails.min_recharge_amount
                      : "-"}
                  </p>
                </div>
                <div className="flex flex-col w-full mb-4">
                  <p className=" text-primary_text font-normal mb-1 text-sm">
                    Maximum Recharge Amount
                  </p>
                  <p className=" text-primary_text font-medium text-sm">
                    {mnoDetails.max_recharge_amount
                      ? "AFA " + mnoDetails.max_recharge_amount
                      : "-"}
                  </p>
                </div>
                <h1 className="text-heading3 mb-4 underline underline-offset-4 decoration-gray-400">
                  MNO Address
                </h1>
                <div className="flex flex-col w-full mb-4">
                  <p className=" text-primary_text font-normal mb-1 text-sm">
                    Address
                  </p>
                  <p className=" text-primary_text font-medium w-[400px] text-sm">
                    {addressString || "-"}
                  </p>
                </div>
                <hr className="border-t-1 mt-2 border-gray-300 w-full" />
              </div>
              <div className="px-6 pb-2 pt-6 w-full">
                <h1 className="text-heading3 mb-4 underline underline-offset-4 decoration-gray-400">
                  Service Fee
                </h1>
                <h1 className="text-heading3 mb-4 underline underline-offset-4 decoration-gray-400">
                  eTop Up
                </h1>
                <div className="flex flex-wrap w-full">
                  <div className="flex flex-col mb-4 w-1/3">
                    <p className=" text-primary_text font-normal mb-1 text-sm">
                      Upfront Service Fee Percentage
                    </p>
                    <p className=" text-primary_text font-medium text-sm">
                      {mnoDetails.etopup_upfront_fee_percent
                        ? mnoDetails.etopup_upfront_fee_percent + "%"
                        : "-"}
                    </p>
                  </div>
                  <div className="flex flex-col w-1/3 mb-4">
                    <p className=" text-primary_text font-normal mb-1 text-sm">
                      On Target Achievement Service Fee Percentage
                    </p>
                    <p className=" text-primary_text font-medium text-sm">
                      {mnoDetails.etopup_target_fee_percent
                        ? mnoDetails.etopup_target_fee_percent + "%"
                        : "-"}
                    </p>
                  </div>
                  <div className="flex flex-col w-1/3 mb-4">
                    <p className=" text-primary_text font-normal mb-1 text-sm">
                      Principal Service Fee Percentage
                    </p>
                    <p className=" text-primary_text font-medium text-sm">
                      {mnoDetails.etopup_principal_fee_percent
                        ? mnoDetails.etopup_principal_fee_percent + "%"
                        : "-"}
                    </p>
                  </div>
                  <div className="flex flex-col w-1/3 mb-4">
                    <p className=" text-primary_text font-normal mb-1 text-sm">
                      Dispersive Service Fee Percentage (Postpaid)
                    </p>
                    <p className=" text-primary_text font-medium text-sm">
                      {mnoDetails.etopup_dispersive_fee_percent
                        ? mnoDetails.etopup_dispersive_fee_percent + "%"
                        : "-"}
                    </p>
                  </div>
                </div>
                <h1 className="text-heading3 mb-4 underline underline-offset-4 decoration-gray-400">
                  Bundle Activation
                </h1>
                <div className="flex flex-wrap w-full">
                  <div className="flex flex-col mb-4 w-1/3">
                    <p className=" text-primary_text font-normal mb-1 text-sm">
                      Upfront Service Fee Percentage
                    </p>
                    <p className=" text-primary_text font-medium text-sm">
                      {mnoDetails.bundle_upfront_fee_percent &&
                      mnoDetails.bundle_upfront_fee_percent !== "0.00"
                        ? mnoDetails.bundle_upfront_fee_percent + "%"
                        : "-"}
                    </p>
                  </div>
                  <div className="flex flex-col w-1/3 mb-4">
                    <p className=" text-primary_text font-normal mb-1 text-sm">
                      On Target Achievement Service Fee Percentage
                    </p>
                    <p className=" text-primary_text font-medium text-sm">
                      {mnoDetails.bundle_target_fee_percent &&
                      mnoDetails.bundle_target_fee_percent !== "0.00"
                        ? mnoDetails.bundle_target_fee_percent + "%"
                        : "-"}
                    </p>
                  </div>
                  <div className="flex flex-col w-1/3 mb-4">
                    <p className=" text-primary_text font-normal mb-1 text-sm">
                      Principal Service Fee Percentage
                    </p>
                    <p className=" text-primary_text font-medium text-sm">
                      {mnoDetails.bundle_principal_fee_percent &&
                      mnoDetails.bundle_principal_fee_percent !== "0.00"
                        ? mnoDetails.bundle_principal_fee_percent + "%"
                        : "-"}
                    </p>
                  </div>
                  <div className="flex flex-col w-1/3 mb-4">
                    <p className=" text-primary_text font-normal mb-1 text-sm">
                      Dispersive Service Fee Percentage (Postpaid)
                    </p>
                    <p className=" text-primary_text font-medium text-sm">
                      {mnoDetails.bundle_dispersive_fee_percent &&
                      mnoDetails.bundle_dispersive_fee_percent !== "0.00"
                        ? mnoDetails.bundle_dispersive_fee_percent + "%"
                        : "-"}
                    </p>
                  </div>
                </div>
                <hr className="border-t-1 border-gray-300 w-full" />
              </div>
              <div className="px-6 pb-2 pt-6 w-full">
                <h1 className="text-heading3 mb-4 underline underline-offset-4 decoration-gray-400">
                  MNO Target
                </h1>
                <h1 className="text-heading3 mb-4 underline underline-offset-4 decoration-gray-400">
                  eTop Up
                </h1>
                <div className="flex flex-wrap w-full">
                  <div className="flex flex-col mb-4 w-1/3">
                    <p className=" text-primary_text font-normal mb-1 text-sm">
                      From Date
                    </p>
                    <p
                      className=" text-primary_text font-medium text-sm"
                      data-testid={`e_top_from_date`}
                    >
                      {mnoDetails.etopup_from_date
                        ? formatDateToDDMMYYYY(mnoDetails.etopup_from_date)
                        : "-"}
                    </p>
                  </div>
                  <div className="flex flex-col w-1/3 mb-4">
                    <p className=" text-primary_text font-normal mb-1 text-sm">
                      To Date
                    </p>
                    <p
                      className=" text-primary_text font-medium text-sm"
                      data-testid={`e_top_to_date`}
                    >
                      {mnoDetails.etopup_to_date
                        ? formatDateToDDMMYYYY(mnoDetails.etopup_to_date)
                        : "-"}
                    </p>
                  </div>
                  <div className="flex flex-col w-1/3 mb-4">
                    <p className=" text-primary_text font-normal mb-1 text-sm">
                      eTop up Target Value
                    </p>
                    <p className=" text-primary_text font-medium text-sm">
                      {mnoDetails.etopup_target_value
                        ? "AFA " + mnoDetails.etopup_target_value
                        : "-"}
                    </p>
                  </div>
                </div>
                <h1 className="text-heading3 mb-4 underline underline-offset-4 decoration-gray-400">
                  Bundle Activation
                </h1>
                <div className="flex flex-wrap w-full">
                  <div className="flex flex-col mb-4 w-1/3">
                    <p className=" text-primary_text font-normal mb-1 text-sm">
                      From Date
                    </p>
                    <p
                      className=" text-primary_text font-medium text-sm"
                      data-testid={`bundle_from_date`}
                    >
                      {mnoDetails.bundle_from_date
                        ? formatDateToDDMMYYYY(mnoDetails.bundle_from_date)
                        : "-"}
                    </p>
                  </div>
                  <div className="flex flex-col w-1/3 mb-4">
                    <p className=" text-primary_text font-normal mb-1 text-sm">
                      To Date
                    </p>
                    <p
                      className=" text-primary_text font-medium text-sm"
                      data-testid={`bundle_to_date`}
                    >
                      {mnoDetails.bundle_to_date
                        ? formatDateToDDMMYYYY(mnoDetails.bundle_to_date)
                        : "-"}
                    </p>
                  </div>
                  <div className="flex flex-col w-1/3 mb-4">
                    <p className=" text-primary_text font-normal mb-1 text-sm">
                      Bundle Activation Target Value
                    </p>
                    <p className=" text-primary_text font-medium text-sm">
                      {mnoDetails.bundle_target_value
                        ? "AFA " + mnoDetails.bundle_target_value
                        : "-"}
                    </p>
                  </div>
                </div>
                <hr className="border-t-1 border-gray-300 w-full" />
              </div>
            </div>
          </div>
        ) : (
          <Shimmer />
        )}
        {isModalOpen && (
          <CustomModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            title="Confirm MNO Connection"
            subText={`You're about to connect ${mnoDetails.name} to the selected subnet (${mnoDetails?.subnet}). This will establish a connection between the MNO and the required resources. Once connected, this MNO will be available in the recharge flow and users will be able to select it when recharging mobile numbers. Please confirm if you'd like to proceed.`}
            note="Note: This action cannot be undone."
            primaryButtonText="Yes"
            secondaryButtonText="No"
            handelSubmit={() => handleSubnetConnection()}
            buttonWidth="w-[85px]"
            reverseButton={"w-fit flex-row-reverse"}
          />
        )}
      </div>
    );
}
